import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  assignUpdateForm,
  createUpdateFormHere,
  updateFormConfigurationQuery,
} from '../../api/settingsEaseedeskUpdateForms'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Update ticket form configuration.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * update_form_configuration/index.php`: un rand pe fiecare tip de tichet
 * ACTIV, cu insigna lui colorata, si un `<select>` din care se alege
 * formularul de actualizare. Alegerea se scrie pe loc, ca in `index-1.js`.
 *
 * FORMULAR NOU DE AICI. `create_update_form_here` face formularul si il leaga
 * de tip dintr-o data: cele doua ecrane fac impreuna un singur gest - "tipul
 * asta sa aiba formularul asta" - iar facut si nelegat inseamna un formular
 * ratacit daca omul pleaca intre timp. Se cer amandoua drepturile, deci
 * butonul apare numai cand le are pe amandoua (`canCreateForms`).
 */

/** Fereastra mica: numele formularului nou pentru tipul ales. */
function NewFormForTypeModal({
  type,
  onClose,
  onSaved,
}: {
  type: { ID: number; type_name: string } | null
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')

  useEffect(() => {
    if (type === null) return
    setName('')
  }, [type])

  const create = useMutation({
    mutationFn: () => createUpdateFormHere(type?.ID ?? 0, name.trim()),
    onSuccess: () => {
      toast('New update form created and assigned.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newUpdateFormForTypeModal"
      labelledBy="newUpdateFormForTypeModalLabel"
      open={type !== null}
      onClose={onClose}
      title={`New update form for ${type?.type_name ?? ''}`}
    >
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="update-form-here-name" className="control-label">
            Form name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="update-form-here-name"
            name="form_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Create and assign
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function UpdateFormConfigurationPage() {
  const queryClient = useQueryClient()
  const page = useQuery(updateFormConfigurationQuery())
  const [newFor, setNewFor] = useState<{ ID: number; type_name: string } | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'update_form_configuration'] })
  }

  const assign = useMutation({
    mutationFn: (choice: { type: number; form: number }) => assignUpdateForm(choice.type, choice.form),
    onSuccess: () => {
      toast('Update form assigned.', 'Success')
      reload()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="Update form configuration" />
  if (!page.data) return <Spinner />

  /* `configuration` vine ca lista de perechi, nu ca dictionar: in JSON un
     dictionar cu chei numerice se citeste altfel de la o unealta la alta. */
  const chosen = new Map(page.data.configuration.map((row) => [row.ticket_type_id, row.form_id]))

  return (
    <div>
      <h4>
        <strong>Update ticket</strong> form configuration
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage update <strong>forms</strong>
      </label>
      <hr />

      <table className="table update-forms">
        <thead>
          <tr>
            <th width="100"></th>
            <th className="a-left">Ticket type</th>
            <th>Status form configuration</th>
          </tr>
        </thead>
        <tbody>
          {page.data.ticket_types.map((type) => (
            <tr key={type.ID}>
              <td className="center">
                <span className="badge" style={{ minWidth: 23, color: '#FFF', backgroundColor: type.color_identifier }}>
                  {type.short_name}
                </span>
              </td>
              <td>{type.type_name}</td>
              <td>
                <div className="input-group input-group-sm" style={{ width: '100%' }}>
                  <select
                    className="form-control input-sm change-form-scheme"
                    name="form_id"
                    value={chosen.get(type.ID) ?? 0}
                    onChange={(e) => assign.mutate({ type: type.ID, form: Number(e.target.value) })}
                  >
                    <option value="0">Select form...</option>
                    {page.data.form_list.map((form) => (
                      <option key={form.ID} value={form.ID}>
                        {form.form_name}
                      </option>
                    ))}
                  </select>
                  {page.data.canCreateForms ? (
                    <span className="input-group-btn">
                      <button
                        type="button"
                        className="btn btn-default btn-sm"
                        title="Make an update form that does not exist yet"
                        onClick={() => setNewFor({ ID: type.ID, type_name: type.type_name })}
                      >
                        <i className="fas fa-plus" aria-hidden="true"></i> new
                      </button>
                    </span>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
          {page.data.ticket_types.length === 0 ? (
            <tr>
              <td colSpan={3} className="color-light-grey">
                There is no active ticket type on this instance.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <NewFormForTypeModal type={newFor} onClose={() => setNewFor(null)} onSaved={reload} />
    </div>
  )
}
